import queryString from 'query-string'
import { isEmpty } from 'lodash'

import { fetchingAPI, apiService } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

import {
  SET_NOTIFICATIONS,
  SET_UNREAD_COUNT,
  MARK_READ,
  MARK_ALL_READ,
  RESTORE_UNREAD,
  DISMISS_NOTIFICATION,
  SET_LOADING,
  SET_CURRENT_ORG_USERS,
  SET_CURRENT_ORG_TAGS,
  SET_ALERT_COVERAGE,
} from './notifications.redux'

// Which agents does this manager's RTA alerts cover? Drives the raised_hand
// "set up an alert for this agent" guidance. Aggregates core's per-user
// alert_configs list into { allUsers, userIds, hasTagConfigs } — the same
// all_users / selected-users / tags coverage RTC uses to decide who a manager
// coaches. Non-fatal: on failure we leave alertCoverage unknown so the bell
// neither shows buttons nor the guidance for live-coaching rows.
export const fetchManagerAlertCoverage = (managerId) => async (dispatch) => {
  if (!managerId) {
    return
  }
  try {
    const configs =
      (await fetchingAPI(
        `${apiService.core}/api/alert_configs?${queryString.stringify({ user_id: managerId })}`,
        'GET',
        dispatch
      )) || []
    const coverage = configs.reduce(
      (accumulator, config) => ({
        allUsers: accumulator.allUsers || Boolean(config.all_users),
        userIds: accumulator.userIds.concat(config.users || []),
        hasTagConfigs: accumulator.hasTagConfigs || !isEmpty(config.tags),
      }),
      { allUsers: false, userIds: [], hasTagConfigs: false }
    )
    dispatch({ type: SET_ALERT_COVERAGE, alertCoverage: coverage })
  } catch (error) {
    // Non-fatal — guidance simply won't render.
  }
}

export const fetchUnreadCount = () => {
  return async (dispatch) => {
    try {
      const response = await fetchingAPI(
        `${apiService.notification}/notifications/unread-count`,
        'GET',
        dispatch
      )

      dispatch({ type: SET_UNREAD_COUNT, count: response.count })
    } catch (error) {
      // Silently fail — badge will show stale count
    }
  }
}

export const fetchNotifications = (params = {}) => {
  return async (dispatch) => {
    try {
      dispatch({ type: SET_LOADING, loading: true })

      const query = queryString.stringify({
        read: params.read,
        limit: params.limit || 20,
        offset: params.offset || 0,
        notification_type: params.notificationType,
      })

      const response = await fetchingAPI(
        `${apiService.notification}/notifications/?${query}`,
        'GET',
        dispatch
      )

      dispatch({ type: SET_NOTIFICATIONS, notifications: response.notifications })
      // Refresh the badge count alongside the list. The list is paginated
      // (~20 per page) so it can't be used to recompute the count locally
      // — the dedicated endpoint is the source of truth.
      dispatch(fetchUnreadCount())
    } catch (error) {
      toast.error('Failed to fetch notifications', { cause: error })
    } finally {
      dispatch({ type: SET_LOADING, loading: false })
    }
  }
}

export const markAsRead = (notificationId) => {
  return async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.notification}/notifications/${notificationId}/read`,
        'PATCH',
        dispatch
      )

      dispatch({ type: MARK_READ, notificationId })
    } catch (error) {
      toast.error('Failed to mark notification as read', { cause: error })
    }
  }
}

export const markAllAsRead = () => {
  return async (dispatch, getState) => {
    // Optimistic: clear unread state the moment the user clicks. Gating the
    // dispatch on the PATCH round-trip meant zero feedback until the response
    // arrived — on a slow or dropped response the button looked broken even
    // though the server had already committed the update, and the unreads
    // only "cleared" on the next panel-open refetch (QCER-922).
    //
    // Capture the ids that are unread right now so a failed PATCH can undo just
    // those rows. We re-mark by id rather than restoring a whole click-time
    // snapshot: a notification that arrives via the live WebSocket during the
    // in-flight PATCH then survives the rollback instead of being clobbered.
    // It also needs no second request — a recovery refetch could itself fail
    // (a broader outage would strand the optimistic all-read state) and its own
    // GET-failure toast would double up on this one.
    const unreadIds = getState()
      .notifications.notifications.filter((notification) => !notification.read)
      .map((notification) => notification.id)
    dispatch({ type: MARK_ALL_READ })
    try {
      await fetchingAPI(`${apiService.notification}/notifications/read-all`, 'PATCH', dispatch)
    } catch (error) {
      toast.error('Failed to mark all notifications as read', { cause: error })
      dispatch({ type: RESTORE_UNREAD, notificationIds: unreadIds })
    }
  }
}

export const dismissNotification = (notificationId) => {
  return async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.notification}/notifications/${notificationId}/dismiss`,
        'PATCH',
        dispatch
      )

      dispatch({ type: DISMISS_NOTIFICATION, notificationId })
    } catch (error) {
      toast.error('Failed to dismiss notification', { cause: error })
    }
  }
}

// Loads the user's own-org users + tags into the notifications slice
// (currentOrgUsers / currentOrgTags) for the settings modal's agent/tag
// filter dropdowns. Read commentary on the slice keys for the
// motivation — short version: keep this data isolated from the shared
// organizationUsers / organizationTags slices, which sibling pages can
// repopulate with a different org's data.
//
// The two fetches are independent — a failure on one shouldn't blank
// out the other dropdown. Each runs in its own try/catch and swallows
// errors so the modal still works in a partial-success state (the
// user can fall back to "All Agents in Organization" without picking
// a specific filter).
export const fetchCurrentOrgFilterOptions = (organizationId) => {
  return async (dispatch) => {
    if (!organizationId) {
      return
    }
    const fetchUsersIntoSlice = async () => {
      try {
        const usersResponse = await fetchingAPI(
          `${apiService.core}/api/organizations/${organizationId}/users`,
          'GET',
          dispatch
        )
        dispatch({ type: SET_CURRENT_ORG_USERS, users: usersResponse?.users || [] })
      } catch (error) {
        console.error('Failed to load notification preference user options', error)
      }
    }
    const fetchTagsIntoSlice = async () => {
      try {
        const tagsResponse = await fetchingAPI(
          `${apiService.core}/api/organizations/${organizationId}/tags`,
          'GET',
          dispatch
        )
        dispatch({ type: SET_CURRENT_ORG_TAGS, tags: tagsResponse || [] })
      } catch (error) {
        console.error('Failed to load notification preference tag options', error)
      }
    }
    await Promise.all([fetchUsersIntoSlice(), fetchTagsIntoSlice()])
  }
}
